// UX108 (docs/build-briefs/UX108-refresh-keeps-the-screen.md, section 2.2). The browser side of screenRoute.ts:
// writing the current screen's route to the location hash and hearing Back and Forward.
//
// Who owns what. This module owns the history calls and nothing else. main.ts registers a provider that answers
// "what route is on screen right now" (it composes its own state with admin.ts's); any module that changes
// sub-state of a screen (a Settings group, a report range, an open count, an order detail) calls
// requestRoutePublish() and never builds a hash itself. A screen with no route to publish answers undefined and
// the hash is left alone.
//
// Push or replace. A move to another screen (tabs to a ticket) pushes a history entry so Back works; a change
// inside one screen (another Settings group, another range chip) replaces the entry. The first publish after a
// page load, and the first after a sign-out, always replaces: a refresh never grows the history.

import { formatRoute, parseRoute, sameScreenFamily, type Route } from "./screenRoute.ts";

/** How a page turns its routes into a hash and back. The floor's codec is below; the consoles pass their own
 * (UX134, consoleRoute.ts), so there is one history implementation. */
export interface RouteCodec<R> {
  parse(hash: string): R | undefined;
  format(route: R): string;
  sameScreen(a: R, b: R): boolean;
}

export interface RouteHistory<R> {
  setRouteProvider(fn: () => R | undefined): void;
  setRoutePopHandler(fn: (route: R | undefined) => void): void;
  readInitialRoute(): R | undefined;
  publishRoute(route: R, options?: { replace?: boolean }): void;
  requestRoutePublish(options?: { replace?: boolean }): void;
  withReplaceOnly<T>(fn: () => Promise<T> | T): Promise<T>;
  clearRoute(): void;
  republishLastRoute(): void;
}

export function createRouteHistory<R>(codec: RouteCodec<R>): RouteHistory<R> {
  let provider: (() => R | undefined) | undefined;
  let popHandler: ((route: R | undefined) => void) | undefined;
  let lastPublished: R | undefined;
  /** While above zero, every publish replaces: a restore (Back, Forward, a refresh) must not add entries. */
  let replaceOnly = 0;
  let listening = false;

  function writeHash(hash: string, replace: boolean): void {
    const url = `${window.location.pathname}${window.location.search}${hash}`;
    try {
      if (replace) {
        window.history.replaceState(window.history.state, "", url);
      } else {
        window.history.pushState(null, "", url);
      }
    } catch {
      // A browser that refuses the history API keeps working; the screen simply cannot be restored.
    }
  }

  function publishRoute(route: R, options: { replace?: boolean } = {}): void {
    const hash = codec.format(route);
    if (window.location.hash === hash) {
      lastPublished = route;
      return;
    }
    const replace =
      options.replace === true ||
      replaceOnly > 0 ||
      lastPublished === undefined ||
      codec.sameScreen(lastPublished, route);
    writeHash(hash, replace);
    lastPublished = route;
  }

  function clearRoute(): void {
    lastPublished = undefined;
    if (window.location.hash === "") return;
    writeHash("", true);
  }

  return {
    setRouteProvider(fn) {
      provider = fn;
    },
    /** The page's handler for Back and Forward. It is only called for a hash the codec can parse (or undefined for
     * an address with none), and the page decides whether a session is live. */
    setRoutePopHandler(fn) {
      popHandler = fn;
      if (listening) return;
      listening = true;
      window.addEventListener("popstate", () => {
        popHandler?.(codec.parse(window.location.hash));
      });
    },
    /** The route the page was loaded with. Read it before anything can publish over it. */
    readInitialRoute() {
      return codec.parse(window.location.hash);
    },
    publishRoute,
    /** Asks the registered provider for the route on screen and publishes it. */
    requestRoutePublish(options = {}) {
      const route = provider?.();
      if (route === undefined) return;
      publishRoute(route, options);
    },
    /** Runs `fn` (awaited) with every publish replacing the current entry. */
    async withReplaceOnly<T>(fn: () => Promise<T> | T): Promise<T> {
      replaceOnly += 1;
      try {
        return await fn();
      } finally {
        replaceOnly -= 1;
      }
    },
    /** Removes the hash (a signed-out page must not carry the last person's address). */
    clearRoute,
    /** Puts the address back to the last one published, or clears it when no screen has published one. Used when
     * Back, Forward or an edited address is refused. */
    republishLastRoute() {
      if (lastPublished === undefined) {
        clearRoute();
        return;
      }
      const hash = codec.format(lastPublished);
      if (window.location.hash !== hash) writeHash(hash, true);
    },
  };
}

const floorHistory = createRouteHistory<Route>({
  parse: parseRoute,
  format: formatRoute,
  sameScreen: sameScreenFamily,
});

export const setRouteProvider = floorHistory.setRouteProvider;
export const setRoutePopHandler = floorHistory.setRoutePopHandler;
export const readInitialRoute = floorHistory.readInitialRoute;
export const publishRoute = floorHistory.publishRoute;
export const requestRoutePublish = floorHistory.requestRoutePublish;
export const withReplaceOnly = floorHistory.withReplaceOnly;
export const clearRoute = floorHistory.clearRoute;
export const republishLastRoute = floorHistory.republishLastRoute;
